import React from 'react';
import PropTypes from 'prop-types';
import { Box, FormHelperText, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * BaseField component that serves as the foundation for all field types.
 * Handles common field properties like label, required status, error handling.
 */
const FieldContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: '100%',
}));

// Create a styled component that doesn't pass the error prop to Typography
const FieldLabel = styled(Typography, {
  // Filter out props that shouldn't be passed to the DOM
  shouldForwardProp: (prop) => prop !== 'error' && prop !== 'required'
})(({ theme, required, error }) => ({
  marginBottom: theme.spacing(0.5),
  fontWeight: 500,
  color: error ? theme.palette.error.main : theme.palette.text.primary,
  '&::after': required ? {
    content: '" *"',
    color: theme.palette.error.main,
  } : {},
}));

const BaseField = ({
  id,
  name,
  label,
  required,
  error,
  helperText,
  children,
  disabled,
  fullWidth,
  className,
  labelProps,
  ...rest
}) => {
  return (
    <FieldContainer className={className} {...rest}>
      {label && (
        <FieldLabel
          variant="body2"
          required={required}
          error={Boolean(error)}
          {...labelProps}
        >
          {label}
        </FieldLabel>
      )}
      {children}
      {helperText && (
        <FormHelperText error={Boolean(error)}>
          {helperText}
        </FormHelperText>
      )}
    </FieldContainer>
  );
};

BaseField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  helperText: PropTypes.string,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  labelProps: PropTypes.object,
};

BaseField.defaultProps = {
  required: false,
  disabled: false,
  fullWidth: true,
};

export default BaseField;
